<template>
	<SideNavLayout>
		<div class="more-page">
			<router-link to="/settings" class="more-item">
				<AppIcon :name="IconName.Settings" class="more-icon" />
				<span>{{ $t("settings") }}</span>
			</router-link>

			<button class="more-item" @click="privacy.openSettings()">
				<AppIcon :name="IconName.Settings" class="more-icon" />
				<span>{{ $t("privacy.settings") }}</span>
			</button>

			<button v-if="isUserLoggedIn && config.ui.has(Feature.AppTour)" class="more-item" @click="handleStartAppTour">
				<AppIcon :name="IconName.Info" class="more-icon" />
				<span>{{ $t("appTour.menuLabel") }}</span>
			</button>

			<div v-if="canAccessAdmin" class="more-divider" />

			<router-link
				v-if="canAccessAdmin"
				to="/admin"
				class="more-item"
				@click="analytics.navigationClicked('navigation.admin')"
			>
				<AppIcon :name="IconName.Grid" class="more-icon" />
				<span>{{ $t("adminPage.navLabel") }}</span>
			</router-link>

			<a
				:href="buyerProfileUrl"
				target="_blank"
				class="more-item"
				@click="analytics.navigationClicked('buyer-profile-action')"
			>
				<AppIcon :name="IconName.UserCircle" class="more-icon" />
				<span>{{ $t("buyer-profile-action") }}</span>
			</a>

			<a
				href="https://www.landerz.ca/privacypolicy"
				target="_blank"
				class="more-item"
				@click="analytics.navigationClicked('terms')"
			>
				<AppIcon :name="IconName.FileText" class="more-icon" />
				<span>{{ $t("terms") }}</span>
			</a>

			<div v-if="isUserLoggedIn" class="more-divider" />

			<button v-if="isUserLoggedIn" class="more-item logout-item" @click="handleLogout">
				<AppIcon :name="IconName.LogOut" class="more-icon" :stroke-width="2" />
				<span>{{ $t("logout") }}</span>
			</button>
		</div>
	</SideNavLayout>
</template>

<script setup lang="ts">
import { usePrivacyStore } from "@/stores/privacyStore"
import { computed } from "vue"
import { storeToRefs } from "pinia"
import { useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import SideNavLayout from "@/components/layout/SideNavLayout.vue"
import { IconName } from "@/components/icons/registry"
import { analytics } from "@/utils/analytics"
import { UseRootStore } from "@/model/RootStore"
import { useUIStore } from "@/stores/uiStore"
import { config, Feature } from "@/AppConfig"
import { Route } from "@/utils/routing"
import { hasAdminAccess } from "@/utils/adminAccess"

const privacy = usePrivacyStore()

const router = useRouter()
const store = UseRootStore()
const uiStore = useUIStore()
const { isUserLoggedIn } = storeToRefs(store)
const { locale } = useI18n()

const buyerProfileUrl = computed(() =>
	locale.value === "en" ? "https://en.landerz.ca/profil" : "https://www.landerz.ca/profil"
)

const canAccessAdmin = computed(() => isUserLoggedIn.value && hasAdminAccess(store.currentUser))

async function handleStartAppTour(): Promise<void> {
	analytics.navigationClicked("appTour.menuLabel")
	await router.push({ name: Route.Home })
	uiStore.startAppTour()
}

async function handleLogout(): Promise<void> {
	try {
		await store.logout()
		router.push("/")
	} catch (error) {
		console.error("Error during logout:", error)
		router.push("/login")
	}
}
</script>

<style scoped>
.more-page {
	padding: var(--spacing-sm) 0;
	font-family: var(--font-family-primary);

	.more-item {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		width: 100%;
		padding: var(--spacing-md) var(--spacing-lg);
		border: none;
		background: none;
		text-align: left;
		text-decoration: none;
		color: var(--neutral-600);
		font-size: var(--font-size-base);
		cursor: pointer;

		.more-icon {
			flex-shrink: 0;
			color: currentColor;
		}

		&:active {
			background: var(--neutral-100);
		}
	}

	.logout-item {
		color: var(--accent-warning);
	}

	.more-divider {
		height: var(--border-width-thin);
		background: var(--neutral-200);
		margin: var(--spacing-xs) var(--spacing-lg);
	}
}
</style>
